/**
 * Status = lifecycle `status` × SRI `sriEstado`. Ported verbatim from the
 * mobile `InvoiceStatus.fromServer` so both clients render identically.
 */
export type Estado = 'autorizada' | 'pendiente' | 'anulando' | 'anulada' | 'devuelta' | 'error';

export function estadoFromSri(estado: string | null | undefined): Estado {
  switch ((estado ?? '').trim().toUpperCase()) {
    case 'AUTORIZADO':
      return 'autorizada';
    case 'DEVUELTA':
    case 'DEVUELTO':
    case 'RECHAZADO':
    case 'NO_AUTORIZADO':
    case 'NO AUTORIZADO':
      return 'devuelta';
    case 'ANULADO':
      return 'anulada';
    case 'ERROR':
      return 'error';
    case 'EN_COLA':
    case 'EN_PROCESO':
    case 'RECIBIDA':
    case 'PENDIENTE':
    default:
      return 'pendiente';
  }
}

export function estadoFromServer(status: string | null | undefined, sriEstado: string | null | undefined): Estado {
  switch ((status ?? '').trim().toLowerCase()) {
    case 'recalling':
      return 'anulando';
    case 'recalled':
      return 'anulada';
    case 'dropped':
      return 'error';
  }
  // submitted / resolved (and rows with no lifecycle) → the SRI outcome.
  return estadoFromSri(sriEstado ?? status);
}

export function isTerminal(e: Estado): boolean {
  return e !== 'pendiente' && e !== 'anulando';
}

/** Emit-response `estado` values considered "done" (no more reconciliation needed). */
export function sriEstadoIsTerminal(estado: string | null | undefined): boolean {
  return estadoFromSri(estado) !== 'pendiente';
}

export const ESTADO_TONE: Record<Estado, 'autorizada' | 'pendiente' | 'devuelta' | 'error' | 'anulada' | 'neutral'> = {
  autorizada: 'autorizada',
  pendiente: 'pendiente',
  anulando: 'neutral',
  anulada: 'anulada',
  devuelta: 'devuelta',
  error: 'error',
};

/** Human-readable SRI estado for the post-emit dialog. */
export function describeSriEstado(estado: string): string {
  switch (estado.trim().toUpperCase()) {
    case 'EN_COLA':
      return 'En cola para envío';
    case 'EN_PROCESO':
      return 'En proceso en el SRI';
    case 'AUTORIZADO':
      return 'Autorizado';
    case 'NO_AUTORIZADO':
      return 'No autorizado';
    case 'DEVUELTA':
      return 'Devuelta por el SRI';
    case 'DESCONOCIDO':
      return 'Estado desconocido';
    case 'PENDIENTE':
      return 'Pendiente';
    default:
      return estado;
  }
}
